import { useMemo } from "react";
import { ErrorState, PermissionDeniedState } from "@/components/ui/system-state";
import { Button } from "@/components/ui/button";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { useErrorLocale } from "@/i18n/use-error-locale";
import type { ApiErrorInfo } from "@/lib/api";
import { classifyFailure, failureFromReason, plainErrorMessage } from "@/lib/api-failure";

/**
 * One place that turns a failed read into the right state: PermissionDeniedState for a
 * denial (with the missing capability under "Access details"), otherwise ErrorState with
 * plain-language copy from the error locale catalog and a Retry button. The raw reason
 * code is never shown as the message; the server support reference is shown beside it.
 * Follows the pattern of features/wallets/wallet-request.tsx (ALL-5736).
 */
export function ApiFailure({
  error: rawError,
  subject,
  onRetry,
  action,
}: {
  error: ApiErrorInfo | string;
  /** What could not be read, lower case, e.g. "your wallets". */
  subject: string;
  onRetry?: () => void;
  action?: React.ReactNode;
}) {
  const error = failureFromReason(rawError);
  // Read the locale from the URL directly so the component also works outside a router.
  const search = typeof window === "undefined" ? "" : window.location.search;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const catalog = useErrorLocale(searchParams);
  const kind = classifyFailure(error);

  if (kind === "denied") {
    return (
      <div>
        <PermissionDeniedState
          title="Access required"
          description={`You don't have access to ${subject}. Ask an organization admin to grant it.`}
          details={error.capability ? [{ k: "Needs", v: error.capability }] : undefined}
          action={action ?? (onRetry ? <Button variant="secondary" onClick={onRetry}>Try again</Button> : undefined)}
        />
        <SupportDetails reference={error.supportReference} className="mt-3" />
      </div>
    );
  }

  const copy = kind === "not_found" ? catalog.categories.not_found : catalog.categories.service_unavailable;
  return (
    <div>
      <ErrorState title={copy.title} description={copy.message} onRetry={onRetry} />
      <SupportDetails reference={error.supportReference} className="mt-3" />
    </div>
  );
}

/**
 * ALL-5774: the plain-language message for a failure in the active error locale, for inline
 * notices. Never the raw reason code.
 */
export function usePlainErrorMessage(): (error: { reasonCode?: string | null } | null | undefined) => string {
  const search = typeof window === "undefined" ? "" : window.location.search;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const catalog = useErrorLocale(searchParams);
  return (error) => plainErrorMessage(catalog, error);
}
